import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { ReactNode, useMemo, useState } from 'react';

import { KizeoAdminSession } from '@activepieces/shared';
import { HttpError, api } from '@/lib/api';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { LoadingScreen } from '@/components/ui/loading-screen';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import KhubLogo from '../../../../public/img/custom/logo.svg';

const AccessDenied = () => {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen items-center justify-center">
      <Card className="w-[28rem] rounded-sm drop-shadow-xl">
        <div className="flex flex-col items-center mt-4 gap-1">
          <img src={KhubLogo} className="size-24" alt="Khub" />
          <Badge
            variant="outline"
            className="border-primary/20 bg-primary/10 text-primary"
          >
            {t('Administrator')}
          </Badge>
        </div>
        <CardHeader className="text-center pt-4">
          <CardTitle className="text-2xl">
            {t('Authentication required')}
          </CardTitle>
          <CardDescription>
            {t(
              'To access the Khub Admin Interface, your session on the Kizeo Forms Admin Interface must be active.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button className="w-full">
            <a
              href={`${import.meta.env.VITE_KIZEO_OAUTH_URL}/admin`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Sign in to Kizeo Forms Admin')}
            </a>
          </Button>
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => window.location.reload()}
          >
            {t('Retry')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const isAuthError = (error: unknown): boolean => {
  if (!api.isError(error)) return false;
  const status = (error as unknown as HttpError).response?.status;
  return (
    status === HttpStatusCode.Unauthorized ||
    status === HttpStatusCode.Forbidden
  );
};

export const KizeoAdminAuthGuard = ({ children }: { children: ReactNode }) => {
  const [sessionExpired, setSessionExpired] = useState(false);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            if (isAuthError(error)) {
              setSessionExpired(true);
            }
          },
        }),
      }),
    [],
  );

  const { data, isLoading, isError } = useQuery<KizeoAdminSession>(
    {
      queryKey: ['internal-admin-session'],
      queryFn: () => adminApi.getSession(),
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
    queryClient,
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (sessionExpired || isError || !data) {
    return <AccessDenied />;
  }

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
